import type { RefObject } from "react";
import { useEffect } from "react";

/**
 * Closes a popup on outside `mousedown`.
 *
 * Use this instead of Kendo Popup's built-in `onMouseDownOutside` when the
 * popup lives inside a Kendo `Reveal` / `ExpansionPanel` (or anything else
 * that renders a `.k-animation-container`). Kendo's handler treats any click
 * whose target has a `.k-animation-container` ancestor as "inside" and never
 * fires, so popups nested inside an accordion never close on outside click.
 *
 * @param enabled         Only listens while the popup is open.
 * @param anchorRef       The element that toggles/opens the popup.
 * @param popupRef        The popup content (since the popup is portaled to
 *                        `document.body`, the anchor's `contains` check won't
 *                        cover it).
 * @param onOutsideClick  Called when a `mousedown` lands outside both refs.
 */
export const usePopupOutsideClick = ({
	enabled,
	anchorRef,
	popupRef,
	onOutsideClick,
}: {
	enabled: boolean;
	anchorRef: RefObject<HTMLElement | null>;
	popupRef: RefObject<HTMLElement | null>;
	onOutsideClick: () => void;
}) => {
	useEffect(() => {
		if (!enabled) return;

		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (anchorRef.current?.contains(target)) return;
			if (popupRef.current?.contains(target)) return;
			onOutsideClick();
		};

		document.addEventListener("mousedown", handleMouseDown);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, [enabled, anchorRef, popupRef, onOutsideClick]);
};
